/**
 * Companion header: Figma title bar — history menu (or back on history/knowledge),
 * centered title, new-chat and close. No Chat/History tabs.
 */
import { Add, ArrowLeft, CloseSquare, HambergerMenu } from 'iconsax-react';
import { AGENT_STATE_META } from '../types/agent-state.types';
import type { AgentState } from '../types/agent-state.types';
import type { AssistantView } from '../store/assistant-store';
import { TiaAvatar } from './tia/tia-avatar';
import type { TiaReaction } from './tia/tia-avatar';

export interface CompanionHeaderProps {
  /** Live agent state — drives the (visually hidden) avatar reaction. */
  state: AgentState;
  /** Optional tool narration; replaces the state label while present (FR-11). */
  narration?: string | null;
  /** One-shot micro-reaction forwarded to the avatar (FR-15). */
  reaction?: TiaReaction | null;
  /** Reaction-cycle completion, forwarded from the avatar (FR-15). */
  onReactionEnd?: () => void;
  activeView: AssistantView;
  onViewChange: (view: AssistantView) => void;
  onNewChat: () => void;
  onClose: () => void;
}

const ICON_BUTTON =
  'shrink-0 rounded-lg p-0 text-gray-1000 hover:bg-primary-50';

export function CompanionHeader({
  state,
  narration = null,
  reaction = null,
  onReactionEnd,
  activeView,
  onViewChange,
  onNewChat,
  onClose,
}: CompanionHeaderProps) {
  const meta = AGENT_STATE_META[state];
  const showBack = activeView === 'list';

  return (
    <div
      data-slot='companion-header'
      className='flex h-[69px] shrink-0 items-center gap-2 border-b border-primary-200 px-4'
    >
      <button
        type='button'
        aria-label={showBack ? 'Back' : 'History'}
        onClick={() => {
          if (activeView === 'list') onViewChange('thread');
          else onViewChange('list');
        }}
        className={ICON_BUTTON}
      >
        {showBack ? (
          <ArrowLeft
            size={24}
            className='size-6'
          />
        ) : (
          <HambergerMenu
            size={24}
            className='size-6'
          />
        )}
      </button>

      <p className='min-w-0 flex-1 text-center text-base font-semibold capitalize leading-[1.2] text-gray-1000'>
        Tia - Your ai Assistant
      </p>

      <button
        type='button'
        aria-label='New chat'
        onClick={onNewChat}
        className={ICON_BUTTON}
      >
        <Add
          size={24}
          className='size-6'
        />
      </button>
      <button
        type='button'
        aria-label='Close'
        onClick={onClose}
        className={ICON_BUTTON}
      >
        <CloseSquare
          size={24}
          className='size-6'
        />
      </button>

      {/* Live avatar + status stay in the tree for FR-11/FR-15; Figma header is title-only. */}
      <div className='sr-only'>
        <TiaAvatar
          state={state}
          size={56}
          showLabel={false}
          reaction={reaction}
          onReactionEnd={onReactionEnd}
        />
        <div
          data-slot='companion-status'
          role='status'
          style={{ color: meta.glow }}
        >
          {narration ?? meta.label}
        </div>
      </div>
    </div>
  );
}
